/**
 * Decides whether a window error / unhandledrejection is storefront-owned.
 * Production panic overlay must not paint for extensions or cross-origin noise.
 */

const EXTENSION_SOURCE_RE =
  /(?:chrome|moz|safari-web|safari|ms-browser)-extension:\/\//i

const APP_BUNDLE_HINT_RE = /webpack:\/\/|\/static\/js\//i

const CROSS_ORIGIN_SCRIPT_ERROR_RE = /^script error\.?$/i

const HTTP_URL_RE = /https?:\/\/[^\s)\]'"]+/gi

function readOrigin(): string {
  try {
    return typeof window !== "undefined" ? String(window.location.origin ?? "") : ""
  } catch {
    return ""
  }
}

function stringifyUnknown(value: unknown): string {
  if (value instanceof Error) {
    return `${value.name} ${value.message} ${value.stack ?? ""}`
  }
  if (typeof value === "string") return value
  if (value == null) return ""
  try {
    return String(value)
  } catch {
    return ""
  }
}

export function isExtensionSource(text: string): boolean {
  return EXTENSION_SOURCE_RE.test(text)
}

function isSameOriginUrl(url: string, origin: string): boolean {
  if (!origin || !url) return false
  try {
    const parsed = new URL(url, origin)
    return parsed.origin === origin
  } catch {
    return url.startsWith(origin)
  }
}

function extractHttpUrls(text: string): string[] {
  return text.match(HTTP_URL_RE) ?? []
}

function stackLooksLikeApp(stack: string, origin: string): boolean {
  if (APP_BUNDLE_HINT_RE.test(stack)) return true
  if (origin && stack.includes(origin)) return true
  return false
}

function urlsAreOnlyForeign(stack: string, origin: string): boolean {
  const urls = extractHttpUrls(stack)
  if (urls.length === 0) return false
  return urls.every((url) => !isSameOriginUrl(url, origin))
}

/** True when the window error should not paint the panic overlay. */
export function shouldIgnorePanicErrorEvent(event: ErrorEvent): boolean {
  const filename = String(event.filename ?? "")
  const message = String(event.message ?? "")
  const errorText = stringifyUnknown(event.error)
  const haystack = `${filename}\n${message}\n${errorText}`
  const origin = readOrigin()

  if (isExtensionSource(haystack)) return true

  if (CROSS_ORIGIN_SCRIPT_ERROR_RE.test(message.trim()) && !filename) {
    return true
  }

  if (filename) {
    if (isExtensionSource(filename)) return true
    return !isSameOriginUrl(filename, origin)
  }

  if (event.error instanceof Error) {
    const stack = event.error.stack ?? ""
    if (stackLooksLikeApp(stack, origin)) return false
    if (urlsAreOnlyForeign(stack, origin)) return true
    return false
  }

  return true
}

/** True when the rejection should not paint the panic overlay. */
export function shouldIgnorePanicRejection(reason: unknown): boolean {
  const origin = readOrigin()
  const text = stringifyUnknown(reason)
  if (isExtensionSource(text)) return true

  if (reason instanceof Error) {
    const stack = reason.stack ?? ""
    if (stackLooksLikeApp(stack, origin)) return false
    if (urlsAreOnlyForeign(stack, origin)) return true
    return false
  }

  return true
}
